import type React from "react";
import { useEffect, useRef, useState } from "react";
import { MdLockOpen } from "react-icons/md";

interface UnlockRequestReceivedModalProps {
  isOpen: boolean;
  onReject: () => void; // This will be used for "Reject"
  onAccept: () => void; // This will be used for "Accept" or timeout
  requestingUserDisplayName: string;
}

export const UnlockRequestReceivedModal: React.FC<
  UnlockRequestReceivedModalProps
> = ({ isOpen, onReject, onAccept, requestingUserDisplayName }) => {
  const [countdown, setCountdown] = useState(5);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.showModal();
      setCountdown(5); // Reset countdown when modal opens
    } else {
      dialogRef.current?.close();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (countdown <= 0) {
      onAccept();
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [countdown, isOpen, onAccept]);

  return (
    <dialog ref={dialogRef} className="modal h-full w-full">
      <div className="modal-box max-w-lg bg-white p-0 text-briefly-text-primary">
        <div className="modal-content flex items-start gap-6 px-8 pb-10 pt-12">
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100">
            <MdLockOpen className="h-6 w-6 text-yellow-600" />
          </div>
          <div className="flex flex-col gap-2">
            <h3 className="text-xl font-bold text-briefly-text-primary">
              Access Request
            </h3>
            <p className="text-md text-briefly-text-secondary">
              <strong>{requestingUserDisplayName}</strong> is requesting access
              to the field you are editing. This will close automatically in{" "}
              <strong>({countdown})</strong> seconds.
            </p>
          </div>
        </div>
        <div className="modal-action my-0 flex w-full justify-end bg-gray-100 p-6">
          <button
            type="button"
            className="btn-med text-md mr-3 rounded-md border border-briefly-action bg-white px-6 py-3 text-briefly-text-primary hover:bg-gray-100 focus:outline-none"
            onClick={onReject}
          >
            Reject
          </button>
          <button
            type="button"
            className="btn-med text-md rounded-md border-none bg-briefly-action px-6 py-3 text-white hover:bg-briefly-action/90 focus:outline-none"
            onClick={onAccept}
          >
            Accept
          </button>
        </div>
      </div>
    </dialog>
  );
};
